import React, { useState } from "react";
import {
  IconTrash,
  IconPencil,
  IconCalendar,
  IconPlus,
  IconClock,
  IconCheck,
  IconX,
  IconDotsVertical,
  IconDeviceLaptop,
} from "@tabler/icons-react";

type EmployeeType = {
  _id: string;
  name: string;
  avatar?: string;
  designation: string;
  department: string;
};

type ActivityType = {
  id: string;
  type: "in" | "out" | "break";
  time: string;
  label: string;
  ip?: string;
};

type AttendanceDetailsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  employee?: EmployeeType;
  date?: string; // YYYY-MM-DD
  onEdit?: () => void;
  onDelete?: () => void;
  isSidebar?: boolean;
};

export default function AttendanceDetailsModal({
  isOpen,
  onClose,
  employee,
  date = "2026-05-01",
  onEdit,
  onDelete,
  isSidebar = false,
}: AttendanceDetailsModalProps) {
  const [activities, setActivities] = useState<ActivityType[]>([
    { id: "act-1", type: "in", time: "09:35 AM", label: "Clock In" },
    { id: "act-2", type: "out", time: "11:00 PM", label: "Clock Out · Office Leave" },
  ]);
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);

  if (!isOpen || !employee) return null;

  // Format date display (e.g. 13-07-2026)
  const formattedDate = () => {
    const parts = date.split("-");
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return date;
  };

  const handleAddActivity = () => {
    if (onEdit) onEdit();
  };

  // SVG Donut Chart calculation
  const r = 32;
  const circumference = 2 * Math.PI * r;
  const tealPercent = 80;
  const yellowPercent = 10;

  const tealStrokeOffset = circumference - (circumference * tealPercent) / 100;
  const yellowStrokeOffset = circumference - (circumference * yellowPercent) / 100;

  // The restructured main grid matching the screenshot layout
  const mainContent = (
    <div className="flex flex-col lg:flex-row gap-6 p-6 overflow-y-auto max-h-[calc(85vh-80px)] scrollbar-clean bg-white">
      {/* Left Column: Profile Card + Clock In/Out boxes + Donut Chart */}
      <div className="w-full lg:w-[320px] shrink-0 space-y-4">
        {/* Profile Card */}
        <div className="flex items-center gap-4 bg-white border border-gray-100 rounded-3xl p-5 shadow-xs">
          <div className="relative">
            <div className="w-14 h-14 rounded-full p-[2px] bg-gradient-to-tr from-yellow-400 to-orange-300 shadow-xs">
              <div className="w-full h-full rounded-full overflow-hidden bg-white flex items-center justify-center text-sm font-bold text-[#212121]">
                {employee.avatar ? (
                  <img src={employee.avatar} alt={employee.name} className="w-full h-full object-cover" />
                ) : (
                  employee.name.charAt(0)
                )}
              </div>
            </div>
            <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white"></span>
          </div>
          <div>
            <h3 className="text-base font-bold text-[#212121] truncate max-w-[180px]">
              {employee.name}
            </h3>
            <p className="text-xs text-gray-500 font-semibold truncate max-w-[180px]">
              {employee.designation}
            </p>
            <p className="text-[10px] text-gray-400 font-medium">
              {employee.department}
            </p>
          </div>
        </div>

        {/* Date Info */}
        <div className="flex items-center justify-center gap-2 bg-slate-50 border border-slate-100 rounded-2xl py-2 px-4 text-xs font-semibold text-gray-600">
          <IconCalendar size={15} className="text-[#FBD037]" />
          <span>{formattedDate()}</span>
        </div>

        {/* Clock In / Out Boxes */}
        <div className="grid grid-cols-2 gap-3">
          {/* Clock In Box */}
          <div className="bg-[#3CD69D] text-white rounded-2xl p-4 flex flex-col items-center justify-center text-center shadow-xs">
            <span className="text-[10px] font-bold uppercase tracking-wider opacity-90">Clock In</span>
            <span className="text-lg font-black mt-1">09:35 AM</span>
            <span className="text-[9px] opacity-80 mt-1">Good morning! 💻</span>
          </div>

          {/* Clock Out Box */}
          <div className="bg-[#F35555] text-white rounded-2xl p-4 flex flex-col items-center justify-center text-center shadow-xs">
            <span className="text-[10px] font-bold uppercase tracking-wider opacity-90">Clock Out</span>
            <span className="text-lg font-black mt-1">11:00 PM</span>
            <span className="text-[9px] opacity-80 mt-1">Have a great day! 💻</span>
          </div>
        </div>

        {/* Total Work Donut Chart */}
        <div className="bg-white border border-gray-100 rounded-3xl p-5 shadow-xs flex flex-col items-center justify-center">
          <div className="relative w-28 h-28 flex items-center justify-center">
            <svg className="absolute transform -rotate-90 w-full h-full" viewBox="0 0 80 80">
              <circle
                cx="40"
                cy="40"
                r={r}
                fill="transparent"
                stroke="#E2E8F0"
                strokeWidth="6"
              />
              <circle
                cx="40"
                cy="40"
                r={r}
                fill="transparent"
                stroke="#3CD69D"
                strokeWidth="6"
                strokeDasharray={circumference}
                strokeDashoffset={tealStrokeOffset}
                strokeLinecap="round"
              />
              <circle
                cx="40"
                cy="40"
                r={r}
                fill="transparent"
                stroke="#F59E0B"
                strokeWidth="6"
                strokeDasharray={circumference}
                strokeDashoffset={yellowStrokeOffset}
                strokeLinecap="round"
                style={{ transform: "rotate(40deg)", transformOrigin: "center" }}
              />
            </svg>
            <div className="text-center z-10 flex flex-col leading-tight">
              <span className="text-[9px] uppercase font-bold text-gray-400">Total Work</span>
              <span className="text-xs font-black text-[#3CD69D]">13h 25m</span>
            </div>
          </div>
        </div>

        {/* Action Button: Edit inside Left Panel */}
        <button
          onClick={onEdit}
          className="w-full flex items-center justify-center gap-2 py-3 bg-[#FBD037] hover:bg-[#FACC15] text-[#212121] text-xs font-bold rounded-xl transition cursor-pointer border-none shadow-xs"
        >
          <IconPencil size={14} /> Edit Attendance
        </button>
      </div>

      {/* Right Column: Activity Timeline */}
      <div className="flex-grow space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
            Activity Timeline
          </h4>
          <button
            type="button"
            onClick={handleAddActivity}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black text-white hover:bg-gray-800 transition text-xs font-bold shadow-xs cursor-pointer border-none"
          >
            <IconPlus size={14} /> Activity
          </button>
        </div>

        {/* Timeline Cards Container */}
        <div className="space-y-3">
          {activities.map((activity) => (
            <div
              key={activity.id}
              className="flex items-center justify-between p-4 bg-white border border-gray-100 rounded-2xl shadow-xs"
            >
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full border-2 border-gray-200 flex items-center justify-center bg-white">
                  <div className="w-2.5 h-2.5 rounded-full bg-gray-200" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-[#212121]">
                      {activity.type === "in" ? "⏰ " : activity.type === "out" ? "🚪 " : "⏳ "}
                      {activity.label}
                    </span>
                  </div>
                  <span className="text-[10px] text-gray-400 font-semibold">{activity.time}</span>
                </div>
              </div>

              <div className="flex items-center gap-1.5 relative">
                {activity.type === "in" && (
                  <>
                    <span className="p-1.5 rounded-lg bg-emerald-50 text-emerald-500 border border-emerald-100">
                      <IconDeviceLaptop size={13} stroke={2.5} />
                    </span>
                    <span className="p-1.5 rounded-lg bg-gray-50 text-gray-400 border border-gray-100">
                      <IconClock size={13} stroke={2.5} />
                    </span>
                  </>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveMenuId(activeMenuId === activity.id ? null : activity.id);
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-50 cursor-pointer border-none bg-transparent flex items-center justify-center"
                >
                  <IconDotsVertical size={16} />
                </button>
                {activeMenuId === activity.id && (
                  <>
                    <div
                      className="fixed inset-0 z-40 bg-transparent"
                      onClick={() => setActiveMenuId(null)}
                    />
                    <div className="absolute right-0 top-full mt-1.5 z-50 bg-white border border-gray-100 rounded-xl shadow-lg py-1 w-28 animate-in fade-in duration-100">
                      <button
                        type="button"
                        onClick={() => {
                          setActiveMenuId(null);
                          if (onEdit) onEdit();
                        }}
                        className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition cursor-pointer flex items-center gap-2 border-none bg-transparent font-bold"
                      >
                        <IconPencil size={13} /> Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveMenuId(null);
                          if (onDelete) onDelete();
                        }}
                        className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50 transition cursor-pointer flex items-center gap-2 border-none bg-transparent font-bold"
                      >
                        <IconTrash size={13} /> Delete
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (isSidebar) {
    return (
      <div className="relative rounded-3xl shadow-sm border border-slate-100 flex flex-col overflow-hidden bg-white w-full z-10 animate-in fade-in slide-in-from-right-5 duration-300">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200/50 bg-[#E9F0F8]/30 shrink-0">
          <h2 className="text-xl font-semibold text-[#212121]">Attendance Details</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#D2D2D2] hover:bg-slate-50 cursor-pointer bg-white text-slate-700"
          >
            <IconX size={18} />
          </button>
        </div>
        {mainContent}
      </div>
    );
  }

  // Clean Centered Modal Wrapper matching screenshot
  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl border border-gray-100 flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Custom Header with Delete + Close */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 bg-white">
          <h2 className="text-xl font-bold text-[#212121]">Attendance Details</h2>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onDelete}
              className="w-9 h-9 rounded-full bg-rose-50 text-rose-500 hover:bg-rose-100 transition flex items-center justify-center cursor-pointer border-none"
              title="Delete Log"
            >
              <IconTrash size={18} />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-full bg-gray-50 text-gray-500 hover:bg-gray-100 transition flex items-center justify-center cursor-pointer border-none"
            >
              <IconX size={18} />
            </button>
          </div>
        </div>

        {/* Custom Body */}
        {mainContent}
      </div>
    </div>
  );
}
